import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  Trees,
  Hammer,
  Boxes,
  Wind,
  Wrench,
  Shield,
  Clock,
  MapPin,
} from "lucide-react";
import QuickQuote from "../components/QuickQuote";

const HERO_IMG =
  "https://images.unsplash.com/photo-1724537354210-5563567a8cff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHx3aXNjb25zaW4lMjBmb3Jlc3R8ZW58MHx8fHwxNzgxMzQ4NTU3fDA&ixlib=rb-4.1.0&q=85";
const STORM_IMG =
  "https://images.unsplash.com/photo-1765064519342-deae9d37579b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxzdG9ybSUyMGNsZWFudXAlMjB0cmVlfGVufDB8fHx8MTc4MTM0ODU1N3ww&ixlib=rb-4.1.0&q=85";
const LAND_IMG =
  "https://images.unsplash.com/photo-1668120089662-42642838cfef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxsYW5kc2NhcGluZyUyMG91dGRvb3JzfGVufDB8fHx8MTc4MTM0ODU1N3ww&ixlib=rb-4.1.0&q=85";
const LABOR_IMG =
  "https://images.pexels.com/photos/37499254/pexels-photo-37499254.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BRICK_IMG =
  "https://images.unsplash.com/photo-1615498801364-95844ea36f04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxicmljayUyMHJvYWQlMjB5ZWxsb3d8ZW58MHx8fHwxNzgxMzQ4NTU3fDA&ixlib=rb-4.1.0&q=85";

export const SERVICES = [
  {
    icon: Wind,
    title: "Storm Damage Cleanup",
    blurb:
      "Downed limbs, blown debris, fence wreckage. We clear the yard so you can breathe.",
    span: "md:col-span-3 md:row-span-2",
    img: STORM_IMG,
    big: true,
  },
  {
    icon: Truck,
    title: "Junk Removal",
    blurb: "Garage purge to whole-house haul. Loaded out, hauled, gone.",
    span: "md:col-span-2",
  },
  {
    icon: Boxes,
    title: "Estate Clean Outs",
    blurb:
      "Compassionate, careful work for families navigating a tough transition.",
    span: "md:col-span-2",
  },
  {
    icon: Trees,
    title: "Light Landscaping",
    blurb: "Brush clearing, bed cleanup, mulch, weeding, hauling.",
    span: "md:col-span-3",
    img: LAND_IMG,
  },
  {
    icon: Hammer,
    title: "Small Tree Service",
    blurb: "Saplings, low limbs, and safe removals for a clean, healthy yard.",
    span: "md:col-span-2",
  },
  {
    icon: Wrench,
    title: "Odd Jobs & Manual Labor",
    blurb:
      "Move it, lift it, dig it, build it. Property maintenance of all kinds — we'll talk.",
    span: "md:col-span-2",
  },
];

export default function Home() {
  return (
    <div className="bg-stone-950">
      {/* HERO */}
      <section
        className="relative min-h-[88vh] flex items-end overflow-hidden grain"
        data-testid="hero-section"
      >
        <img
          src={HERO_IMG}
          alt="Wisconsin woods at dusk"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/80 to-stone-950/30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 animate-fade-up">
            <div className="brick-row" aria-hidden="true">
              <span /><span /><span /><span /><span />
            </div>
            <span className="label-eyebrow text-yellow-400">
              Eau Claire, WI · Manual Labor Services
            </span>
          </div>
          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[7.5rem] font-black tracking-tight leading-[0.92] text-stone-50 max-w-6xl animate-fade-up delay-1"
          >
            There really is{" "}
            <span className="italic font-extrabold">no place like home.</span>
          </h1>
          <p className="mt-7 font-display text-2xl sm:text-3xl lg:text-4xl text-yellow-400 max-w-4xl leading-tight tracking-tight animate-fade-up delay-2">
            The odds and ends are our business.
            <br className="hidden sm:block" />
            Let us clear the path back to your yellow brick road.
          </p>
          <p
            data-testid="hero-services-line"
            className="mt-6 text-base sm:text-lg text-stone-300 max-w-3xl leading-relaxed animate-fade-up delay-2"
          >
            Storm cleanup <span className="text-yellow-500">•</span> Junk removal{" "}
            <span className="text-yellow-500">•</span> Estate cleanouts{" "}
            <span className="text-yellow-500">•</span> Brush hauling{" "}
            <span className="text-yellow-500">•</span> Light landscaping{" "}
            <span className="text-yellow-500">•</span> Small tree work
          </p>

          {/* Free estimate phone CTA — high-intent visitors decide in seconds */}
          <a
            href="tel:7152146563"
            data-testid="hero-phone-cta"
            className="mt-8 inline-flex items-center gap-4 bg-yellow-500 hover:bg-yellow-400 text-yellow-950 border-2 border-yellow-500 px-6 py-4 transition-colors animate-fade-up delay-3 group max-w-full"
          >
            <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-extrabold bg-yellow-950/15 px-2 py-1">
              <span className="inline-block h-2 w-2 rounded-full bg-green-600" />
              Free Estimates
            </span>
            <span className="flex flex-col items-start leading-tight">
              <span className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-80">
                Call or Text
              </span>
              <span className="font-display text-2xl sm:text-3xl font-black tracking-tight">
                (715) 214-6563
              </span>
            </span>
          </a>
          <div className="mt-6 flex flex-wrap gap-4 animate-fade-up delay-3">
            <Link
              to="/book"
              data-testid="hero-book-btn"
              className="inline-flex items-center gap-2 bg-transparent text-stone-50 border-2 border-stone-500 font-bold uppercase tracking-wider px-6 py-3 hover:border-yellow-400 hover:text-yellow-300 transition-colors text-sm"
            >
              Book Online <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="inline-flex items-center gap-2 bg-transparent text-stone-300 border-2 border-stone-700 font-bold uppercase tracking-wider px-6 py-3 hover:border-stone-400 hover:text-white transition-colors text-sm"
            >
              View Services
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl animate-fade-up delay-4">
            {[
              { Icon: Shield, label: "Honest Pricing" },
              { Icon: Clock, label: "Fast Response" },
              { Icon: MapPin, label: "Eau Claire + Travel" },
              { Icon: Hammer, label: "Property Maintenance" },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 text-stone-300">
                <Icon className="h-5 w-5 text-yellow-400" />
                <span className="text-xs uppercase tracking-[0.18em] font-bold">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="py-24 lg:py-32 bg-stone-950" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="label-eyebrow mb-3">What We Do</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-stone-50 tracking-tight max-w-2xl">
                Hard work, plain talk, and a path that gets you home.
              </h2>
            </div>
            <p className="text-stone-400 max-w-md leading-relaxed">
              We specialize in property cleanup, debris hauling, landscaping
              support, storm recovery, and general property maintenance
              throughout Eau Claire and surrounding communities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:auto-rows-[180px]">
            {SERVICES.map((s) => (
              <ServiceCard key={s.title} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* RECENT PROJECTS — real before/after */}
      <section
        className="py-24 lg:py-32 bg-stone-900 border-t border-stone-800"
        data-testid="recent-projects-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="label-eyebrow mb-3">Recent Projects</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-stone-50 tracking-tight max-w-2xl">
                Real work. Real photos. <span className="text-yellow-400">No filters.</span>
              </h2>
            </div>
            <p className="text-stone-400 max-w-md leading-relaxed">
              Every "after" on this page comes from an actual job we did in
              Wisconsin. As we wrap more cleanups, we'll add them here.
            </p>
          </div>

          <BeforeAfter
            beforeSrc="https://customer-assets.emergentagent.com/wingman/3c786696-2342-4aac-b0ec-87fcbef1c969/attachments/806102e65d674b0db172d21f88093994_IMG_20260604_091242278.jpg"
            afterSrc="https://customer-assets.emergentagent.com/wingman/3c786696-2342-4aac-b0ec-87fcbef1c969/attachments/7d9aa16087924ad09c52d8ea78b0a8aa_IMG_20260607_203613271.jpg"
            tag="Yard Cleanup"
            location="Eau Claire, WI"
            summary="Overgrown brush and yard debris hauled out, beds cleared, ground reset for an open, usable lawn."
          />

          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book?service=Light+Landscaping"
              data-testid="recent-projects-quote-btn"
              className="inline-flex items-center gap-2 bg-yellow-500 text-yellow-950 font-bold uppercase tracking-wider px-6 py-3 border-2 border-yellow-500 hover:bg-yellow-400 transition-colors text-sm"
            >
              Get a Cleanup Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BRICK ROAD BANNER */}
      <section className="relative h-[36vh] min-h-[280px] overflow-hidden grain" data-testid="brick-banner">
        <img
          src={BRICK_IMG}
          alt="Yellow brick path"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-950/70" />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center">
          <div className="label-eyebrow mb-4">Our Promise</div>
          <p className="font-display text-3xl sm:text-4xl lg:text-5xl text-stone-50 max-w-3xl leading-tight">
            "There's no place like home — let us take care of it."
          </p>
        </div>
      </section>

      {/* QUICK QUOTE — primary funnel */}
      <section className="py-20 lg:py-28 bg-stone-950" data-testid="quick-quote-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <div className="label-eyebrow mb-3">Free Estimate</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-stone-50 tracking-tight">
              Ten seconds. <span className="text-yellow-400">Same-day callback.</span>
            </h2>
            <p className="mt-5 text-stone-400 leading-relaxed">
              Don't feel like filling out a long form? Drop your name, number,
              and what you need. We'll call or text you back today with a real
              estimate — no pressure, no spam.
            </p>
          </div>
          <div className="lg:col-span-3">
            <QuickQuote />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 lg:py-32 bg-stone-900" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="label-eyebrow mb-3">How It Works</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-stone-50 tracking-tight max-w-2xl mb-14">
            Three steps. No drama.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                n: "01",
                t: "Tell us the job",
                d: "Submit the form or call. Share photos if you've got them.",
              },
              {
                n: "02",
                t: "Lock in a date",
                d: "We'll confirm timing and pricing. Optional deposit holds your spot.",
              },
              {
                n: "03",
                t: "We show up & finish it",
                d: "On time. Cleaned up after. You get your weekend back.",
              },
            ].map((step) => (
              <div
                key={step.n}
                className="border border-stone-700 bg-stone-800 p-8 hover:-translate-y-1 hover:border-yellow-500/60 transition-all"
              >
                <div className="font-display text-yellow-400 text-5xl font-black mb-4">
                  {step.n}
                </div>
                <h3 className="font-display text-2xl font-semibold text-stone-50 mb-2">
                  {step.t}
                </h3>
                <p className="text-stone-400 leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OWNER'S NOTE — replaces testimonials until real ones exist */}
      <section className="py-24 lg:py-32 bg-stone-950" data-testid="owner-note-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="label-eyebrow mb-3">A Note From The Owner</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-stone-50 tracking-tight mb-8">
            New shop. Old-school work ethic.
          </h2>
          <div className="space-y-5 text-stone-300 text-lg leading-relaxed">
            <p>
              Oz and Ends is brand new, which means you won't find a wall of
              five-star reviews here yet — and I'm not going to fake one to
              make this page feel busier.
            </p>
            <p>
              What I will promise: I show up when I say I will, I quote
              honestly, and I clean up after myself. If you'd like to be one
              of the first jobs on the books, the phone goes straight to me.
            </p>
            <p className="text-stone-500 text-sm italic pt-2">
              Real reviews from real Eau Claire neighbors will land on this
              page as we earn them.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32 bg-stone-900 border-t border-stone-800" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="brick-row mx-auto mb-6 justify-center" aria-hidden="true">
            <span /><span /><span /><span /><span /><span /><span />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-stone-50 tracking-tight">
            Got a mess? <span className="text-yellow-400">We've got hands.</span>
          </h2>
          <p className="mt-5 text-stone-300 text-lg max-w-2xl mx-auto leading-relaxed">
            Free consultations. Same-week scheduling when we can swing it. Call,
            text, or book online.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/book"
              data-testid="cta-book-btn"
              className="inline-flex items-center gap-2 bg-yellow-500 text-yellow-950 font-bold uppercase tracking-wider px-8 py-4 border-2 border-yellow-500 hover:bg-yellow-400 transition-colors"
            >
              Get a Free Estimate <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ icon: Icon, title, blurb, span = "", img, big = false }) {
  return (
    <article
      data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`relative overflow-hidden border border-stone-700 bg-stone-800 p-6 lg:p-8 flex flex-col justify-between hover:-translate-y-1 hover:border-yellow-500/60 transition-all min-h-[220px] ${span}`}
    >
      {img && (
        <>
          <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/70 to-stone-900/30" />
        </>
      )}
      <div className="relative">
        <Icon className="h-7 w-7 text-yellow-400 mb-4" />
        <h3
          className={`font-display font-semibold text-stone-50 tracking-tight ${
            big ? "text-3xl sm:text-4xl" : "text-2xl"
          }`}
        >
          {title}
        </h3>
        <p className="mt-3 text-stone-300 text-sm leading-relaxed max-w-md">
          {blurb}
        </p>
      </div>
      <div className="relative mt-6">
        <Link
          to={`/book?service=${encodeURIComponent(title)}`}
          data-testid={`service-card-inquire-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="inline-flex items-center gap-1 text-yellow-400 text-xs uppercase tracking-[0.2em] font-bold hover:text-yellow-300"
        >
          Get a Quote <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </article>
  );
}


function BeforeAfter({ beforeSrc, afterSrc, tag, location, summary }) {
  return (
    <div
      data-testid="before-after-card"
      className="border border-stone-700 bg-stone-950 overflow-hidden"
    >
      <div className="grid md:grid-cols-2">
        <PhotoTile src={beforeSrc} label="Before" tone="before" testid="before-photo" />
        <PhotoTile src={afterSrc} label="After" tone="after" testid="after-photo" />
      </div>
      <div className="p-6 lg:p-8 border-t border-stone-800 grid md:grid-cols-3 gap-6 items-start">
        <div>
          <div className="label-eyebrow">Job Type</div>
          <div className="mt-1 font-display text-xl text-stone-50 font-semibold">{tag}</div>
        </div>
        <div>
          <div className="label-eyebrow">Location</div>
          <div className="mt-1 font-display text-xl text-stone-50 font-semibold">{location}</div>
        </div>
        <p className="md:col-span-1 text-stone-300 text-sm leading-relaxed">{summary}</p>
      </div>
    </div>
  );
}

function PhotoTile({ src, label, tone, testid }) {
  const isBefore = tone === "before";
  return (
    <div className="relative group">
      <div className="aspect-[4/5] sm:aspect-[4/3] overflow-hidden bg-stone-800">
        <img
          src={src}
          alt={label}
          loading="lazy"
          data-testid={testid}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
      </div>
      <div className="absolute top-4 left-4">
        <span
          className={`inline-flex items-center gap-2 px-3 py-1.5 font-display text-sm font-bold uppercase tracking-[0.22em] border-2 ${
            isBefore
              ? "bg-stone-950/85 text-stone-100 border-stone-600"
              : "bg-yellow-500 text-yellow-950 border-yellow-500"
          }`}
        >
          {isBefore ? <span className="h-1.5 w-1.5 rounded-full bg-stone-400" /> : <span className="h-1.5 w-1.5 rounded-full bg-green-700" />}
          {label}
        </span>
      </div>
    </div>
  );
}
